/**
 * In-memory holder for the auth values the framework-agnostic API layer needs.
 *
 * `createApiClient` reads the access token and affiliate id synchronously through
 * these getters, while the React `AuthProvider` bridge (`useAuthStoreSync`) keeps
 * them current. Storing the values here (module state) rather than in React state
 * keeps the axios client decoupled from React and avoids the v1 smell of
 * re-reading tokens from `sessionStorage` strings in several places.
 *
 * The snapshot is process-local and deliberately not persisted: after a full page
 * reload the bridge repopulates it from the restored OIDC session.
 */
export interface AuthSnapshot {
  /** Current OIDC bearer token, or `null` when unauthenticated. */
  readonly accessToken: string | null;
  /** Current affiliate (tenant) id, or `null` when none is selected. */
  readonly affiliateId: string | null;
}

const EMPTY_SNAPSHOT: AuthSnapshot = {
  accessToken: null,
  affiliateId: null,
};

let currentSnapshot: AuthSnapshot = EMPTY_SNAPSHOT;

/** Replaces the stored snapshot. Called by the auth bridge on every auth change. */
export function setAuthSnapshot(snapshot: AuthSnapshot): void {
  currentSnapshot = snapshot;
}

/** Resets the snapshot to the logged-out state. */
export function clearAuthSnapshot(): void {
  currentSnapshot = EMPTY_SNAPSHOT;
}

type ReauthenticationHandler = () => void;

let reauthenticationHandler: ReauthenticationHandler | null = null;

/**
 * Registers (or clears, with `null`) the handler invoked by
 * {@link requestReauthentication}. The auth bridge registers one that starts a
 * fresh sign-in; when none is registered (for example in isolated tests) the
 * request is a no-op.
 */
export function setReauthenticationHandler(
  handler: ReauthenticationHandler | null,
): void {
  reauthenticationHandler = handler;
}

/** Asks the app to re-authenticate. Called by the API client on a 401 response. */
export function requestReauthentication(): void {
  reauthenticationHandler?.();
}

/** The current bearer token, or `null` when unauthenticated. */
export function getAccessToken(): string | null {
  return currentSnapshot.accessToken;
}

/** The current affiliate (tenant) id, or `null` when none is selected. */
export function getAffiliateId(): string | null {
  return currentSnapshot.affiliateId;
}
